import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  FileText, 
  BookOpen, 
  UserCheck, 
  Flag, 
  HardDrive,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-500 text-sm font-medium mb-1">{label}</p>
        <p className="text-3xl font-bold text-slate-900">{value}</p>
        {subValue && (
          <p className="text-xs text-slate-400 mt-1">{subValue}</p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
    {trend && (
      <div className={`flex items-center gap-1 mt-3 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
        {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        <span>{Math.abs(trend)}% from yesterday</span>
      </div>
    )}
  </div>
);

export default function AdminDashboard({ admin }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('dharmbook_admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, { headers }),
        axios.get(`${API}/admin/audit-logs?limit=10`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setRecentActivity(logsRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Welcome back, {admin?.name}</p>
        </div>
        <div className="text-sm text-slate-500">
          {new Date().toLocaleDateString('en-IN', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats?.total_users || 0}
          subValue={`+${stats?.new_users_today || 0} today`}
          color="bg-blue-500"
        />
        <StatCard
          icon={FileText}
          label="Total Posts"
          value={stats?.total_posts || 0}
          color="bg-green-500"
        />
        <StatCard
          icon={BookOpen}
          label="Sacred Texts"
          value={stats?.total_granths || 0}
          color="bg-amber-500"
        />
        <StatCard
          icon={UserCheck}
          label="Verified Pandits"
          value={stats?.total_pandits || 0}
          color="bg-purple-500"
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={Flag}
          label="Pending Reports"
          value={stats?.reported_content_pending || 0}
          color={stats?.reported_content_pending > 0 ? "bg-red-500" : "bg-slate-400"}
        />
        <StatCard
          icon={HardDrive}
          label="Storage Used"
          value={`${stats?.storage_used_mb || 0} MB`}
          color="bg-indigo-500"
        />
        <StatCard
          icon={Activity}
          label="Active Today"
          value={stats?.active_users_today || '-'}
          color="bg-teal-500"
        />
      </div>

      {/* Alerts */}
      {stats?.reported_content_pending > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">Attention Required</p>
            <p className="text-red-600 text-sm">
              {stats.reported_content_pending} reported content item(s) pending review.
            </p>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {admin?.role === 'super_admin' && recentActivity.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Recent Admin Activity</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {recentActivity.map((log, index) => (
              <div key={log.id || index} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 text-sm font-medium">
                    {log.admin_name?.charAt(0) || 'A'}
                  </div>
                  <div>
                    <p className="text-sm text-slate-900">
                      <span className="font-medium">{log.admin_name}</span>
                      {' '}{log.action}{' '}
                      <span className="text-slate-500">{log.entity_type}</span>
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(log.created_at).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <a
            href="/admin/users"
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-xl text-center transition-colors"
          >
            <Users className="w-6 h-6 mx-auto text-blue-500 mb-2" />
            <span className="text-sm font-medium text-slate-700">Manage Users</span>
          </a>
          <a
            href="/admin/posts"
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-xl text-center transition-colors"
          >
            <FileText className="w-6 h-6 mx-auto text-green-500 mb-2" />
            <span className="text-sm font-medium text-slate-700">Moderate Posts</span>
          </a>
          <a
            href="/admin/granths"
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-xl text-center transition-colors"
          >
            <BookOpen className="w-6 h-6 mx-auto text-amber-500 mb-2" />
            <span className="text-sm font-medium text-slate-700">Add Granth</span>
          </a>
          <a
            href="/admin/reports"
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-xl text-center transition-colors"
          >
            <Flag className="w-6 h-6 mx-auto text-red-500 mb-2" />
            <span className="text-sm font-medium text-slate-700">View Reports</span>
          </a>
        </div>
      </div>
    </div>
  );
}
